import React, { memo, useCallback, useMemo } from "react";
import { SavedFormResponse } from "@markit/common.types";
import { useTheme } from "../../hooks/useTheme";
import { CopyCustomLinkify } from "../Links/CopyCustomLinkify";
import CustomLinkify from "../Links/CustomLinkify";
import { downloadMediaFromFirestore } from "../../utils/mediaUtils";
import { Colors } from "../../utils/colors";
import { isFirebaseStorageUrl } from "@markit/common.utils";

type EssentialDataResponseProps = {
  formResponse: SavedFormResponse | null | undefined;
  userFullName: string;
  copyable?: boolean; // also means it's in view profile and should be bolder
  detailView?: boolean; // used to show full response and not one line preview
};

const EssentialDataResponse = memo(function EssentialDataResponseFn(
  props: EssentialDataResponseProps
) {
  const { formResponse, userFullName, copyable, detailView } = props;
  const { theme } = useTheme();

  const noResponse = useMemo(
    () =>
      formResponse === null ||
      formResponse === undefined ||
      (formResponse &&
        formResponse.responses.every((response: any) => response === "")),
    [formResponse]
  );

  const styles = {
    bodyText: {
      color: noResponse
        ? theme.SubText.color
        : copyable
          ? theme.PrimaryText.color
          : theme.SubText.color,
      fontSize: 14,
      fontWeight: copyable ? 500 : 400,
    },
  };

  const isFileResponse = useMemo(() => {
    return formResponse?.responses?.some((response) =>
      isFirebaseStorageUrl(response)
    );
  }, [formResponse]);

  const formResponseToShow = useMemo(
    () =>
      noResponse
        ? "Not Collected"
        : isFileResponse
          ? "View File"
          : formResponse
            ? formResponse.responses.join(", ")
            : "",
    [isFileResponse, formResponse, noResponse]
  );

  const handleDownload = useCallback(async () => {
    if (!formResponse || !isFileResponse) return;
    await downloadMediaFromFirestore(
      formResponse.responses || [],
      `${userFullName}-${formResponse.id}`
    );
  }, [formResponse, isFileResponse, userFullName]);

  if (isFileResponse) {
    return (
      <span
        style={{
          ...styles.bodyText,
          color: Colors.BLUE5,
          cursor: "pointer",
          width: "fit-content",
        }}
        onClick={handleDownload}
      >
        View File
      </span>
    );
  }

  return copyable ? (
    <CopyCustomLinkify
      text={formResponseToShow}
      textStyles={{ ...styles.bodyText, width: "360px" }}
      disableCopy={noResponse}
      showFullContent={detailView}
    />
  ) : (
    <div style={{ maxWidth: "18vw" }}>
      <CustomLinkify>
        <span
          className={!detailView ? "TextOverflowEllipsis" : ""}
          style={styles.bodyText}
        >
          {formResponseToShow}
        </span>
      </CustomLinkify>
    </div>
  );
});

export default EssentialDataResponse;
